<template>
  <nav class="flex-1 flex flex-col bg-white border-r-2 border-orange p-2 shadow-lg shadow-orange overflow-y-auto">
    <div class="h-full">
      <SidebarItem
        v-for="item in userItems"
        :key="item.name"
        :expanded="expanded"
        :name="item.name"
        :notifications="item.notifications"
        :to="item.to"
        @update:expanded="$emit('update:expanded', $event)"
      >
        <template #icon>
          <component :is="item.icon" />
        </template>
      </SidebarItem>
    </div>
    <div v-if="isAdmin">
      <div class="w-auto border-gray-600/20 border-b mx-4" />

      <SidebarItem
        v-for="item in adminItems"
        :key="item.name"
        :expanded="expanded"
        :name="item.name"
        :notifications="item.notifications"
        :to="item.to"
        @update:expanded="$emit('update:expanded', $event)"
      >
        <template #icon>
          <component :is="item.icon" />
        </template>
      </SidebarItem>
      <div class="w-auto border-gray-600/20 border-b mx-4" />
    </div>
    <div>
      <SidebarItem
        :expanded="expanded"
        name="Hilfe"
        to="/feedback"
        @update:expanded="$emit('update:expanded', $event)"
      >
        <template #icon>
          <Lifebuoy />
        </template>
      </SidebarItem>
      <SidebarItem
        :expanded="expanded"
        name="Mein Konto"
        to="/my-account"
        @update:expanded="$emit('update:expanded', $event)"
      >
        <template #icon>
          <AccountOutline />
        </template>
      </SidebarItem>
      <SidebarItem
        :expanded="expanded"
        name="Meine Firma"
        to="/my-company"
        @update:expanded="$emit('update:expanded', $event)"
      >
        <template #icon>
          <Garage />
        </template>
      </SidebarItem>
      <SidebarItem
        clickable
        :expanded="expanded"
        name="Ausloggen"
        @click="logout"
        @update:expanded="$emit('update:expanded', $event)"
      >
        <template #icon>
          <Logout />
        </template>
      </SidebarItem>
    </div>
    <div class="w-auto border-gray-600/20 border-b mx-4" />
    <SidebarItem
      :expanded="expanded"
      :name="authStore.name || authStore.email || 'Kein Name'"
      @update:expanded="$emit('update:expanded', $event)"
    >
      <template #icon>
        <CarvisAvatar
          aria-label="sidebar-avatar"
          :avatar-image-id="authStore.avatarImageId"
          class="w-6 h-6 shrink-0"
          :fallback="{ seed: authStore.userId }"
        />
      </template>
    </SidebarItem>
    <div
      v-if="sm && expanded"
      class="fixed left-0 bottom-0 w-full bg-gray-300 h-1"
    />
  </nav>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import AccountGroupOutline from 'vue-material-design-icons/AccountGroupOutline.vue'
import AccountOutline from 'vue-material-design-icons/AccountOutline.vue'
import CarOutline from 'vue-material-design-icons/CarOutline.vue'
import Factory from 'vue-material-design-icons/Factory.vue'
import FileDocumentEditOutline from 'vue-material-design-icons/FileDocumentEditOutline.vue'
import Garage from 'vue-material-design-icons/Garage.vue'
import GraphOutline from 'vue-material-design-icons/GraphOutline.vue'
import HomeOutline from 'vue-material-design-icons/HomeOutline.vue'
import InboxMultipleOutline from 'vue-material-design-icons/InboxMultipleOutline.vue'
import Lifebuoy from 'vue-material-design-icons/Lifebuoy.vue'
import Logout from 'vue-material-design-icons/Logout.vue'
import { useRouter } from 'vue-router'

import SidebarItem from '@/components/sidebar/SidebarItem.vue'
import { useAuthStore } from '@/stores/auth-store'
import { useNotificationsStore } from '@/stores/notifications-store'

defineProps<{
  expanded: boolean
  sm: boolean
}>()

const authStore = useAuthStore()
const router = useRouter()
const notificationStore = useNotificationsStore()

const { isAdmin } = storeToRefs(authStore)

const logout = async (): Promise<void> => {
  await authStore.logout()
}

const userItems = computed(() => {
  return [
    { name: 'Dashboard', to: '/dashboard', icon: HomeOutline },
    { name: 'Fahrzeuge', to: '/cars', icon: CarOutline, notifications: notificationStore.newCarsNotifications },
    { name: 'Entwürfe', to: '/drafts', icon: FileDocumentEditOutline, notifications: notificationStore.carDraftsCount },
    { name: 'Mein Netzwerk', to: '/my-network', icon: GraphOutline, notifications: notificationStore.myNetworkNotifications },
    { name: 'Firmen', to: '/companies', icon: Factory, notifications: notificationStore.newCompaniesNotifications }
  ]
    .filter(({ to }) => {
      const requiresRole = router.getRoutes().find(route => route.path === to)?.meta.requiresRole
      if (!requiresRole) return true
      return authStore.hasRole(requiresRole)
    })
})

const adminItems = computed(() => {
  return [
    { name: 'Benutzerverwaltung', to: `/user-management/${notificationStore.newSignupsNotifications ? 'signups' : 'users'}`, icon: AccountGroupOutline, notifications: notificationStore.newSignupsNotifications },
    { name: 'Firmenverwaltung', to: '/company-management', icon: Factory },
    { name: 'Nutzerfeedback', to: '/user-feedback', icon: InboxMultipleOutline, notifications: notificationStore.feedbackMessagesNotifications }
  ]
    .filter(({ to }) => {
      const requiresRole = router.getRoutes().find(route => route.path === to)?.meta.requiresRole
      if (!requiresRole) return true
      return authStore.hasRole(requiresRole)
    })
})

defineEmits(['update:expanded'])
</script>
